import type { Language } from '@eformulieren/engine'
import type { CriterionOutcome, Field } from '@eformulieren/model'
import type { ReactNode } from 'react'
import { SlotIcon } from '../icons/SlotIcon.js'
import { inLanguage } from '../language/text.js'

/**
 * De markering per gewijzigde vraag, en de vorige uitslag ernaast (R-178a, R-97, R-98).
 *
 * `determineDifference` staat sinds M1.8 in de engine en had tot M4.10 geen afnemer in beeld. Dit is
 * die afnemer, en het is met opzet de eerste stap en niet de rijke verschilweergave: R-178a
 * vraagt de markering per gewijzigde vraag, en die is uit twee uitgeschreven standen te
 * berekenen en werkt dus altijd. De weergave naast elkaar met doorgehaalde verwijderingen komt
 * uit de snapshots en zit in de geschiedenismodus (M4.11b, ontwerp 6.9); die werkt binnen één
 * generatie en valt bij een compactie halverwege een beurt terug op deze markering.
 *
 * **Wat deze lezer niet mag zien komt niet als verschil in beeld** (R-179). Dat wordt hier niet
 * afgeleid: de pod levert de gewijzigde velden al geprojecteerd, want hij is de enige die de
 * vorige stand heeft.
 *
 * **De vorige uitslag is voor de toetser en niet voor de invuller.** Wat de invuller bij zijn
 * antwoord ziet is een opmerking en geen oordeel; het oordeel reist langs zijn eigen weg naar
 * het processysteem (R-182, R-187, en het besluit staat bij M4.2). De pod levert de uitslagen
 * daarom alleen aan een toetser, en dit component tekent wat het krijgt.
 */

const CHANGED = { nl: 'Gewijzigd sinds de vorige versie', en: 'Changed since the previous version' }
const PREVIOUS_RESULT = { nl: 'Vorige uitslag', en: 'Previous outcome' }
const PASSED = { nl: 'voldeed', en: 'passed' }
const NOT_PASSED = { nl: 'voldeed niet', en: 'did not pass' }

export interface DifferenceMarkerProps {
  readonly taal: Language
  /**
   * Het id van deze markering, zodat een veld naar haar kan wijzen via `aria-describedby`
   * (WCAG 1.3.1, M8.12). Zonder id blijft de tekst geldige HTML, maar staat ze alleen in de
   * visuele leesorde naast het veld en hoort een schermlezer die rechtstreeks naar het veld
   * springt haar nooit.
   */
  readonly id?: string
}

/**
 * De markering bij één gewijzigde vraag.
 *
 * Als tekst en niet als kleur (R-139). Hij staat naast het label en niet in het veld, want een
 * invoerveld met een woord erin is een invoerveld met een woord erin; dit gaat over de vraag en
 * niet over het antwoord.
 */
export function DifferenceMarker({ taal: language, id }: DifferenceMarkerProps): ReactNode {
  return (
    <span id={id} className="ef-verschil" data-verschil="gewijzigd">
      {inLanguage(CHANGED, language)}
    </span>
  )
}

const PREFILLED = {
  nl: 'Voorgevuld vanuit de link waarmee dit formulier is geopend',
  en: 'Prefilled from the link used to open this form',
}

/**
 * De markering bij één voorvuld veld (R-37).
 *
 * Zelfde vorm als {@link DifferenceMarker} en om dezelfde reden naast het label: dit gaat over
 * de vraag en niet over het antwoord, en een invoerveld met een woord erin blijft een invoerveld
 * met een woord erin. Vaste tekst en geen aangeleverde reden: dit product kent het scherm van
 * waaruit de link is geopend niet (dat weet alleen het systeem dat de link maakte), dus een vrije
 * `reden`-parameter in de querystring zou alleen een tweede kanaal voor willekeurige tekst in
 * browsergeschiedenis en logs openen, precies wat ontwerp 3.4 al bij het dossier-id afwijst.
 */
export function PrefillMarker({ taal: language, id }: DifferenceMarkerProps): ReactNode {
  return (
    <span id={id} className="ef-voorvuld" data-voorvuld="voorgevuld">
      {inLanguage(PREFILLED, language)}
    </span>
  )
}

const FROM_PROCESS_SYSTEM_LOCKED = {
  nl: 'Alleen-lezen, uit het processysteem',
  en: 'Read-only, from the process system',
}

const FROM_PROCESS_SYSTEM_TAKEN_OVER = {
  nl: 'Overgenomen uit het processysteem',
  en: 'Taken over from the process system',
}

const FROM_PROCESS_SYSTEM_EDITABLE = {
  nl: 'Overgenomen uit het processysteem, aan te passen',
  en: 'Taken over from the process system, can be changed',
}

/**
 * De markering bij een veld van het processysteem (R-54, R-58, U11).
 *
 * R-58 vraagt dat een overgenomen waarde herkenbaar is als overgenomen, met de bron erbij: als
 * niet-bewerkbaar, of bij een aanpasbaar veld als aanpasbaar. Zelfde plek en vorm als
 * {@link PrefillMarker}, want het gaat over waar de waarde vandaan komt en niet over de waarde. Het
 * niet-aanpasbare veld krijgt het slot van de verwijzing ernaast: het is net zo alleen-lezen, alleen
 * is de bron het processysteem en niet een bouwblok. Afgeleid uit de definitie en niet uit de stand:
 * ook een veld dat het processysteem leeg liet, komt van daar.
 */
export function DossierMarker({
  taal: language,
  id,
  aanpasbaar,
  lezen: reading = false,
}: DifferenceMarkerProps & {
  readonly aanpasbaar: boolean
  /** Of de lezer de sectie alleen leest: dan zegt de markering niet "aan te passen", want voor hem is dat niet zo. */
  readonly lezen?: boolean
}): ReactNode {
  const text = !aanpasbaar ? FROM_PROCESS_SYSTEM_LOCKED : reading ? FROM_PROCESS_SYSTEM_TAKEN_OVER : FROM_PROCESS_SYSTEM_EDITABLE
  return (
    <span id={id} className="ef-dossierveld" data-aanpasbaar={aanpasbaar ? 'ja' : 'nee'}>
      {aanpasbaar ? null : <SlotIcon aria-hidden="true" />}
      {inLanguage(text, language)}
    </span>
  )
}

const ONLY_INTERNAL = {
  nl: 'Niet zichtbaar voor externen',
  en: 'Not visible to external users',
}

/**
 * De markering extern-zichtbaar, bij één veld dat dat juist niet is (R-170, D-54).
 *
 * Alleen bij de uitzondering en niet bij elk veld: een badge op elk veld zou de badge zelf
 * betekenisloos maken, dezelfde reden waarom `reden` op een sectie drie waarden heeft en geen
 * vierde voor "niets bijzonders". De afwezigheid van deze markering zegt dus "gewoon extern
 * zichtbaar", zoals de afwezigheid van {@link DifferenceMarker} "ongewijzigd" zegt.
 *
 * Zelfde vorm als {@link DifferenceMarker} en om dezelfde reden: tekst en niet alleen kleur
 * (R-139), naast het label en niet in het veld. Alleen zichtbaar in de interne weergave; wie het
 * formulier extern invult ziet deze markering nooit, want voor hem is elk veld dat hij ziet per
 * definitie voor hem zichtbaar.
 */
export function InternalMarker({ taal: language, id }: DifferenceMarkerProps): ReactNode {
  return (
    <span id={id} className="ef-alleen-intern" data-alleen-intern="ja">
      {inLanguage(ONLY_INTERNAL, language)}
    </span>
  )
}

export interface PreviousResultProps {
  readonly uitkomst: CriterionOutcome
  readonly taal: Language
  /**
   * Het leesbare label van de vorige waarde (`verdictLabel`), of niets. Bij een tweekeuze zegt het
   * label zelf al of het voldeed; bij een cijferbereik staat dat er tussen haakjes achter.
   */
  readonly label?: string
  readonly tweekeuze?: boolean
}

/**
 * De vorige uitslag van één criterium, naast het criterium zelf (R-178a, R-187).
 *
 * Met de onderbouwing erbij, want de uitslag zonder onderbouwing zegt de toetser niets over wat
 * er vorige keer aan schortte. Zonder menselijk oordeel is er geen vorige uitslag: een
 * machine-uitkomst stelt vast en beslist niet (R-180), en die van de vorige beurt hoort niet als
 * uitslag te worden gelezen.
 */
export function PreviousResult({ uitkomst: outcome, taal: language, label, tweekeuze = false }: PreviousResultProps): ReactNode {
  const verdict = outcome.oordeel
  if (verdict === undefined) return null
  // Nooit de ruwe waarde van de optie ("voldoet-niet"): het label als dat er is, en anders alleen
  // of het voldeed (audit van 30 oktober 2026).
  const settled = inLanguage(verdict.voldoet ? PASSED : NOT_PASSED, language)
  const text = label === undefined ? settled : tweekeuze ? label : `${label} (${settled})`
  return (
    <div className="ef-vorigeuitslag" data-voldoet={verdict.voldoet ? 'ja' : 'nee'}>
      <p className="ef-vorigeuitslag__kop">
        {inLanguage(PREVIOUS_RESULT, language)}: {text}
      </p>
      <p className="ef-vorigeuitslag__onderbouwing">{verdict.onderbouwing}</p>
    </div>
  )
}

const EARLIER = { nl: 'Eerder ingediend', en: 'Submitted earlier' }
const NOT_AS_TEXT = {
  nl: 'dit antwoord is niet als tekst te tonen',
  en: 'this answer cannot be shown as text',
}

/**
 * Eén antwoord uit een eerdere versie, naast het antwoord van nu (R-96).
 *
 * **Per vraag en niet als tweede formulier.** "Een eerdere versie naast de huidige leggen"
 * (C.1 S38) is hier: bij elke vraag staat wat er de vorige keer stond. Een tweede formulier
 * ernaast zou een tweede projectie zijn, en een gedaante erbij is een modus erbij en geen kopie
 * (R-138a). Bovendien is dit de vorm die met een schermlezer werkt: het oude antwoord staat in
 * de leesorde bij de vraag waar het over gaat.
 *
 * **Alleen wat als tekst te tonen is.** Een tekst, een getal, een ja of nee en een lijst daarvan
 * gaan als tekst; een rijke tekst, een bestand of een samengestelde waarde niet, en dan staat er
 * dát in plaats van een lege regel of een `[object Object]`. Wat er niet staat is dus benoemd en
 * niet stil weggelaten; de rijke weergave hangt aan de geschiedenismodus (ontwerp 6.9) en niet
 * hieraan.
 */
export interface EarlierValueProps {
  readonly waarde: unknown
  /**
   * Het veld waar dit antwoord bij hoort.
   *
   * Nodig om een keuzewaarde als label te tonen. Wat er in een revisie staat is de `waarde` van
   * een optie en niet haar label, want taal is een weergavekeuze en het antwoord mag er niet van
   * afhangen (R-143, R-144). In beeld hoort dus het label te staan: een invuller die vorige keer
   * "Eigen bijdrage" koos hoort niet `eigen` te lezen.
   */
  readonly veld: Field
  readonly taal: Language
  /** De kop voor het antwoord; standaard "Eerder ingediend", bij een stand uit de geschiedenis "Toen". */
  readonly kop?: Readonly<Record<'nl' | 'en', string>>
}

export function EarlierValue({ waarde: value, veld: field, taal: language, kop: header = EARLIER }: EarlierValueProps): ReactNode {
  const text = asText(value, field, language)
  return (
    <p className="ef-eerdere" data-eerdere={text === undefined ? 'onvertoonbaar' : 'tekst'}>
      <span className="ef-eerdere__kop">{inLanguage(header, language)}:</span>{' '}
      <span className="ef-eerdere__waarde">{text ?? inLanguage(NOT_AS_TEXT, language)}</span>
    </p>
  )
}

/**
 * Een bevroren antwoord als tekst, of niets als dat niet kan.
 *
 * **De vier vormen van `Antwoord` komen hier langs** (`revisie.ts`): een enkele waarde, een
 * verzameling waarden, documenten, of rijen. Alleen de laatste levert geen tekst: een
 * herhaalgroep is een tabel en geen regel, en die hoort in de geschiedenismodus (ontwerp 6.9) en
 * niet in één regel bij de vraag. De andere drie worden wel getoond, en tot deze correctie niet:
 * de pagina pakte `bevroren.waarde` uit, dus een meerkeuzeantwoord en een bijlage kwamen hier als
 * `undefined` aan en verdwenen achter "niet als tekst te tonen".
 *
 * Een keuzewaarde wordt haar label, in de taal van de lezer; staat de waarde niet meer in de
 * optielijst, dan blijft de waarde zelf staan. Dat laatste is met opzet geen lege regel: het is
 * een antwoord dat is gegeven op een lijst die daarna is gewijzigd, en dát is informatie.
 *
 * Ja en nee gaan ook door de taal. Ze staan hier en niet in `woorden.ts`, want ze zijn nergens
 * anders in de renderer nodig: de schakelaar zelf toont zijn antwoord met een vinkje en niet met
 * een woord.
 */
function asText(response: unknown, field: Field, language: Language): string | undefined {
  if (typeof response === 'string') return labelOf(response, field, language)
  if (typeof response === 'number') return String(response)
  if (typeof response === 'boolean') {
    return response ? inLanguage({ nl: 'ja', en: 'yes' }, language) : inLanguage({ nl: 'nee', en: 'no' }, language)
  }
  if (Array.isArray(response)) {
    const parts = response.map((part) => asText(part, field, language))
    if (parts.some((part) => part === undefined)) return undefined
    return parts.join(', ')
  }
  if (typeof response !== 'object' || response === null) return undefined

  const record = response as Record<string, unknown>
  // Eén waarde. De herkomst (`door`, `gewijzigdOp`) blijft buiten beeld: dit is het antwoord en
  // niet wie het gaf; wie wat wanneer deed staat in de versielijst.
  if ('waarde' in record) return asText(record['waarde'], field, language)
  if ('waarden' in record) return asText(record['waarden'], field, language)
  if ('documenten' in record) return fileNames(record['documenten'], language)
  // `rijen` valt hier met opzet door: een tabel is geen regel.
  return undefined
}

/** De bestandsnamen van een bijlageantwoord, want de hash zegt een invuller niets (U25). */
function fileNames(documents: unknown, language: Language): string | undefined {
  if (!Array.isArray(documents)) return undefined
  if (documents.length === 0) return inLanguage({ nl: 'geen bestand', en: 'no file' }, language)
  const names = documents.map((document) => {
    if (typeof document !== 'object' || document === null) return undefined
    const name = (document as Record<string, unknown>)['bestandsnaam']
    return typeof name === 'string' ? name : undefined
  })
  if (names.some((name) => name === undefined)) return undefined
  return names.join(', ')
}

/** Het label van deze keuzewaarde, of de waarde zelf als het veld er geen opties heeft. */
function labelOf(value: string, field: Field, language: Language): string {
  if (!('opties' in field) || field.opties === undefined) return value
  const option = field.opties.find((candidate) => candidate.waarde === value)
  return option === undefined ? value : inLanguage(option.label, language)
}

const ABOUT_QUESTION = { nl: 'Over de vraag', en: 'About the question' }

/**
 * Over welke vraag dit criterium een uitspraak doet (D-55).
 *
 * Een toetscriterium hangt aan een sectie of aan een veld. Bij een sectie is dat af te lezen aan
 * de plek: de toetssectie staat achter de sectie die zij toetst (R-189). Bij een veld is dat
 * nergens te zien, want het criterium staat in de toetssectie en de vraag ergens anders. Vandaar
 * deze regel: hij noemt de vraag bij naam, zodat de toetser niet hoeft te raden waar zijn oordeel
 * over gaat en de invuller bij een teruglegging dezelfde vraag terugvindt als de werkverzameling
 * hem aanwijst.
 *
 * De naam komt van buiten en niet uit dit component: het label staat op een veld in een andere
 * sectie, en alleen de schil om alle secties heen kan dat opzoeken.
 */
export interface DesignatedQuestionProps {
  readonly naam: string
  readonly taal: Language
}

export function DesignatedQuestion({ naam: name, taal: language }: DesignatedQuestionProps): ReactNode {
  return (
    <p className="ef-overvraag">
      <span className="ef-overvraag__kop">{inLanguage(ABOUT_QUESTION, language)}:</span>{' '}
      <span className="ef-overvraag__naam">{name}</span>
    </p>
  )
}
